import { mapLeaderboardItem } from 'components/Leaderboard/ContestLeaderboard';
import {
  isValidLeaderboardGameType,
  LeaderboardGameType,
} from 'components/Leaderboard/LeaderboardGameLogic';
import { addDays } from 'date-fns';
import { useInfiniteQuery, useQuery } from 'react-query';
import { getArchivedContestLeaderboard } from 'utils/archivedLeaderboards';
import {
  getGames,
  getLeaderboard,
  getUserScore,
  LEADERBOARD_PAGE_SIZE,
  Leaderboard as PlayfabLeaderboard,
} from 'utils/contestAPI';
import { queryKeys } from './QueryClient';

export type Contest = {
  version: number;
  startDate: Date;
  endDate: Date;
};

export type Leaderboard = {
  title: string;
  description: string;
  name: string;
  contests: Contest[];
  latestVersion: number;
};

export type Game = {
  id: string;
  name: string;
  type: LeaderboardGameType;
  leaderboards: Leaderboard[];
};

const getGamesContests = async (): Promise<Game[]> => {
  const games = await getGames();
  return games.reduce((all, game) => {
    const gameType = game.gameTitle.toLocaleLowerCase().replace(' ', '-');
    if (isValidLeaderboardGameType(gameType)) {
      const contest = {
        id: game.gameId,
        name: game.gameTitle,
        type: gameType,
        leaderboards:
          game.leaderboardsMetadata?.map((leaderboard) => ({
            title: leaderboard.title,
            description: leaderboard.description,
            name: leaderboard.name,
            latestVersion: leaderboard.contests ? leaderboard.contests.length - 1 : 0,
            contests:
              leaderboard.contests?.map((contest) => ({
                version: contest.version,
                startDate: new Date(contest.startDateUTC),
                endDate: new Date(contest.endDateUTC),
              })) ?? [],
          })) ?? [],
      };
      all.push(contest);
    }
    return all;
  }, [] as Game[]);
};

export const useContestContext = () => {
  return useQuery(queryKeys.games(), () => {
    return getGamesContests();
  });
};

// Leaderboard versions that are over one version older than the latest version
// are not returned by playfab. We need to manually download these leaderboards
// and serve them manually.
export const isArchivedLeaderboard = (version: number, latestVersion: number) => {
  return latestVersion - version > 1;
};

export const useLeaderboard = (
  type: LeaderboardGameType,
  {
    gameId,
    contestName,
    version,
    latestVersion,
  }: {
    gameId: string;
    contestName: string;
    version: number;
    latestVersion: number;
  },
) => {
  const useArchivedData = isArchivedLeaderboard(version, latestVersion);

  const {
    data: leaderboardData,
    isFetched,
    isFetching,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery(
    ['leaderboard', gameId, contestName, version],
    ({ pageParam = 0 }) => {
      if (useArchivedData) {
        return [];
      }

      return getLeaderboard({
        gameId,
        pageIndex: pageParam,
        contestName,
        version,
      });
    },
    {
      getNextPageParam: (lastPage, pages) => {
        if (lastPage.length < LEADERBOARD_PAGE_SIZE) {
          // indicating no more elements
          return;
        }
        return pages.length;
      },
    },
  );

  const leaderboard = leaderboardData?.pages.flat();
  const runs = leaderboard?.map((x) => mapLeaderboardItem(type, x));

  const { data: userRun } = useQuery(['userScore', gameId, contestName], () =>
    getUserScore(gameId, contestName, version),
  );

  if (useArchivedData) {
    return {
      isFetching: false,
      hasNextPage: false,
      fetchNextPage: () => {},
      isFetched: true,
      runs: getArchivedContestLeaderboard(contestName, version),
      bestRun: null,
    };
  }

  return {
    isFetching,
    hasNextPage,
    fetchNextPage,
    isFetched,
    runs,
    timing: {
      startDate: new Date(),
      endDate: addDays(new Date(), 7),
    },
    bestRun: getBestRun(type, userRun),
  };
};

const getBestRun = (type: LeaderboardGameType, userRun?: PlayfabLeaderboard) => {
  if (!userRun) {
    return null;
  }

  // PlayFab returns 0 as the StatValue for users who aren't on the leaderboard.
  // This is indistinguishable from users who actually have a score of 0, but
  // luckily our two contests should never return that score. Twighlight Shift
  // always returns a negative StatValue and Beast Battler always returns a
  // positive StatValue for valid leaderboard runs.
  if (userRun.StatValue === 0) {
    return null;
  }

  return mapLeaderboardItem(type, userRun);
};
